/**
 * RequireAuth — route guard that redirects unauthenticated users to
 * /login with a `?return_to=<original-route>` hint.
 *
 * Per STORY-001-009 AC #15:
 *   "Given the user has never logged in, when they visit a protected
 *    route, then they are redirected to `/login` with a
 *    `?return_to=<original-route>` query param."
 *
 * Usage:
 *   <Route path="/account/*" element={<RequireAuth><AccountPage /></RequireAuth>} />
 *
 * The guard subscribes to `selectIsAuthenticated` only, not the token
 * string itself, so a token rotation does not trigger a re-render.
 *
 * First-load UX (stay logged in across reloads):
 *   The auth-store starts with `accessToken: null` (the token lives in JS
 *   memory only), so a hard reload / direct-link to a protected route would
 *   naively redirect to /login even with a valid HttpOnly refresh cookie.
 *   `bootstrapAuth()` (api-client, called once in main.tsx) attempts a
 *   silent refresh on boot and flips the store's `bootstrapped` flag. This
 *   guard shows a brief pending state while the token is null AND the boot
 *   refresh is still in flight, and only redirects once it has completed
 *   without a session — preserving AC #15 for genuinely unauthenticated
 *   visitors.
 */
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";

import {
  selectAuthBootstrapped,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";

export interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Full-viewport pending state shown while the boot-time silent refresh is
 * in flight (see `bootstrapAuth` in api-client). Brief — only on protected
 * routes during a hard reload / direct-link, before the refresh resolves.
 */
export function AuthBootstrapPending(): ReactElement {
  const { t } = useTranslation("common");
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t("loading")}
      data-testid="auth-bootstrap-pending"
      className="flex min-h-screen w-full items-center justify-center"
    >
      <span
        aria-hidden="true"
        className="size-8 animate-spin rounded-full border-2 border-muted border-t-primary"
      />
    </div>
  );
}

export function RequireAuth({ children }: RequireAuthProps): ReactElement {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const location = useLocation();

  // A token in memory → through (fresh login OR a session restored by the
  // boot refresh). No need to wait on the bootstrap flag in that case.
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // No token yet AND the one-shot boot refresh hasn't finished: the user may
  // still have a valid HttpOnly refresh cookie. Show a pending state rather
  // than bounce to /login — otherwise a hard reload / direct-link logs a
  // logged-in user out. `bootstrapAuth()` always flips `bootstrapped`, so this
  // cannot hang. (Implements the silent-refresh-on-boot this guard's original
  // docstring deferred to STORY-001-010.)
  if (!bootstrapped) {
    return <AuthBootstrapPending />;
  }

  // No token and the boot refresh is done → genuinely unauthenticated.
  const returnTo = location.pathname + location.search;
  const params = new URLSearchParams({ return_to: returnTo });
  return <Navigate to={"/login?" + params.toString()} replace />;
}

export default RequireAuth;
